import { Link, useNavigate } from 'react-router-dom';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EcranEnErreur,
  EnteteEcran,
  Pastille,
} from '../components/ui/Primitives';
import { ErreurApi } from '../lib/api';
import { useDevoirsDuFormateur, type LigneDevoirDuFormateur } from '../lib/useCours';

const TITRE = 'Quiz et exercices';

function statut(ligne: LigneDevoirDuFormateur): { texte: string; ton: string } {
  if (!ligne.publie) return { texte: 'Brouillon', ton: 'neutral' };
  return ligne.visible ? { texte: 'Publié', ton: 'success' } : { texte: 'Publié · cours non publié', ton: 'warning' };
}

/**
 * « Quiz et exercices » du formateur (écran `quiz`) : les quiz et devoirs de
 * ses cours, la direction tous. Ils se créent et se modifient à l'étape 4
 * « Évaluations & quiz » de chaque cours ; leurs travaux se corrigent dans
 * « Corrections ». L'écran affiche ce que l'API compte.
 */
export function InstructorQuizPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, error } = useDevoirsDuFormateur();

  if (isLoading) return <Chargement libelle="Chargement des quiz et devoirs…" />;
  if (error || !data) {
    return (
      <EcranEnErreur
        titre={TITRE}
        message={error instanceof ErreurApi ? error.message : 'Les quiz et devoirs n’ont pas pu être chargés.'}
      />
    );
  }

  const tous = data.perimetre === 'TOUS';
  const { compteurs } = data;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={TITRE}
        sousTitre={
          tous
            ? 'Les quiz et devoirs de tous les cours en ligne'
            : 'Les quiz et devoirs de vos cours : ils se créent à l’étape « Évaluations & quiz » de chaque cours'
        }
        actions={
          <>
            <BoutonSecondaire onClick={() => navigate('/instructor-grading')}>Corrections</BoutonSecondaire>
            <BoutonPrincipal onClick={() => navigate('/instructor-courses')}>{tous ? 'Cours en ligne' : 'Mes cours'}</BoutonPrincipal>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Compteur libelle="Quiz" valeur={compteurs.quiz} ton="blue" />
        <Compteur libelle="Devoirs à rendre" valeur={compteurs.devoirs} ton="mint" />
        <Compteur libelle="Brouillons" valeur={compteurs.brouillons} detail="non visibles des apprenants" />
        <Compteur libelle="À corriger" valeur={compteurs.aCorriger} ton={compteurs.aCorriger > 0 ? 'warm' : 'navy'} />
      </div>

      <Carte titre="Évaluations" sansBordure>
        {data.devoirs.length === 0 ? (
          <p className="p-4 text-sm text-codialis-gray sm:p-6">
            Aucun quiz ni devoir pour le moment : ils se créent à l’étape « Évaluations & quiz » d’un cours.
          </p>
        ) : (
          <div className="relative overflow-x-auto" role="region" aria-label="Quiz et devoirs" tabIndex={0}>
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Évaluation</th>
                  <th className="px-4 py-3 text-left font-bold">Cours</th>
                  {tous && <th className="px-4 py-3 text-left font-bold">Auteur</th>}
                  <th className="px-4 py-3 text-left font-bold">Échéance</th>
                  <th className="px-4 py-3 text-left font-bold">Statut</th>
                  <th className="px-4 py-3 text-right font-bold">Rendus</th>
                  <th className="px-4 py-3 text-right font-bold">À corriger</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.devoirs.map((ligne) => {
                  const etat = statut(ligne);
                  return (
                    <tr key={ligne.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-codialis-navy">{ligne.titre}</div>
                        <div className="mt-0.5 text-xs text-codialis-gray">
                          {ligne.typeLibelle}
                          {ligne.type === 'QUIZ' ? ` · ${ligne.questions} question${ligne.questions > 1 ? 's' : ''}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-codialis-navy">{ligne.cours.titre}</div>
                        <div className="mt-0.5 text-xs text-codialis-gray">{ligne.module ?? 'Fin du cours'}</div>
                      </td>
                      {tous && <td className="px-4 py-3 text-xs text-codialis-gray">{ligne.auteur}</td>}
                      <td className="whitespace-nowrap px-4 py-3 text-codialis-navy">{ligne.echeanceLisible}</td>
                      <td className="px-4 py-3">
                        <Pastille texte={etat.texte} ton={etat.ton} />
                      </td>
                      <td className="px-4 py-3 text-right text-codialis-navy">{ligne.rendus}</td>
                      <td className="px-4 py-3 text-right">
                        {ligne.aCorriger > 0 ? (
                          <Link to="/instructor-grading" className="font-semibold text-amber-700 hover:underline">
                            {ligne.aCorriger}
                          </Link>
                        ) : (
                          <span className="text-codialis-gray">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/instructor-create?cours=${encodeURIComponent(ligne.cours.id)}&etape=4`}
                          className="inline-flex min-h-[36px] items-center rounded-lg border border-gray-200 px-3 text-xs font-semibold text-codialis-navy hover:bg-gray-50"
                          aria-label={`Ouvrir « ${ligne.titre} » dans son cours`}
                        >
                          Ouvrir
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>
    </div>
  );
}
